import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Gift, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { RegisterForm } from "./Register";

const PERKS = [
  "Your own profile page with up to 8 photos",
  "Listed in searches by productions near you",
  "Private inbox and email alerts for enquiries",
  "No card needed — free for your first 12 months",
  "Open to adults aged 18 and over",
];

const InvalidInvite = () => (
  <div className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="invite-invalid">
    <h1 className="font-display text-4xl font-bold">This invite isn't valid</h1>
    <p className="text-white/60 mt-4">It may have already been used or switched off. You can still join extras.tv as a regular member.</p>
    <Link to="/register" className="inline-block mt-10 bg-primary text-primary-foreground px-8 py-4 font-bold uppercase text-sm tracking-wider" data-testid="invite-regular-register">Join extras.tv</Link>
  </div>
);

export default function Invite() {
  const { code } = useParams();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);

  useEffect(() => {
    api.get(`/invites/${code}`).then((r) => setInvite(r.data)).catch(() => setInvite({ valid: false }));
  }, [code]);

  if (!invite) return <div className="min-h-[60vh] grid place-items-center label-xs">Loading…</div>;
  if (!invite.valid) return <InvalidInvite />;

  return (
    <div className="grid md:grid-cols-2 min-h-[calc(100vh-4rem)]" data-testid="invite-page">
      <div className="relative min-h-[40vh]">
        <img src="/images/talent_male_2.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative h-full flex flex-col justify-end p-8 md:p-14">
          <span className="inline-flex items-center gap-2 self-start bg-primary text-primary-foreground px-3 py-1.5 text-xs font-bold uppercase tracking-widest fade-up"><Gift size={14} /> Special invite</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-6 leading-[0.95] text-shadow fade-up" style={{ animationDelay: "100ms" }} data-testid="invite-title">
            {invite.name ? `${invite.name.split(" ")[0]}, you're` : "You're"} invited to <span className="text-primary">extras.tv</span>
          </h1>
          <p className="mt-5 text-white/80 max-w-md text-shadow fade-up" style={{ animationDelay: "180ms" }}>Your first year of membership is on us.</p>
        </div>
      </div>
      <div className="flex items-center px-6 md:px-16 py-16">
        <div className="w-full max-w-md fade-up" style={{ animationDelay: "200ms" }}>
          <p className="label-xs text-primary">Free for 12 months</p>
          <ul className="mt-5 space-y-3" data-testid="invite-perks">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-white/80"><Check size={16} className="text-primary mt-0.5 shrink-0" />{p}</li>
            ))}
          </ul>
          <div className="mt-10">
            {user ? (
              <p className="text-white/60 text-sm" data-testid="invite-logged-in">You're logged in as {user.email}. Log out first to accept this invite with a new account.</p>
            ) : (
              <RegisterForm inviteCode={code} initial={invite} submitLabel="Claim my free year" />
            )}
          </div>
          <p className="text-xs text-white/40 mt-6">After your free year you can choose to subscribe to stay listed. No card is needed now, and nothing is charged unless you choose to subscribe.</p>
        </div>
      </div>
    </div>
  );
}
